/**
 * Doc 03 §2/§5 — seasonal **skins**.
 *
 * The plan replaced the 25 one-off seasonal templates with *skins*: a named,
 * reusable seasonal treatment (a colour palette + a headline + an optional
 * decorative asset) that can be laid over **any** template. So template #1 can
 * be "Spotlight · Christmas" without a separate Christmas template.
 *
 * A skin is stored on the menu as `design_overrides.skin = "<id>"` and applied
 * by `applyDesignOverrides()` (lib/menu-themes/apply.ts): the skin's colours are
 * merged *under* the merchant's explicit colour overrides (explicit wins), and
 * its `headline`/`decor` are exposed to the renderer as template variables so a
 * template can opt in via `${headline}`.
 *
 * Decor assets live in the Storage `template-assets` bucket (doc 03 §"Decor
 * assets to create"); where an asset does not exist yet the `decor` key is
 * still recorded but nothing renders it (same standing blocker as the catalog
 * templates — omitted, not broken).
 *
 * Colours are limited to the six semantic slots the Design panel exposes
 * (`DESIGN_COLOR_KEYS`) so a skin flows through the exact same override path as
 * a hand-picked palette. `skinSwatch` gives the picker a bg/accent/text chip.
 */

import { DESIGN_COLOR_KEYS, type DesignColorKey } from '@/lib/menu-themes/design-overrides';

export type SkinColors = Partial<Record<DesignColorKey, string>>;

/** A fixed-date default window for the "Use from … to …" control (MM-DD). */
export interface SkinDefaultWindow {
  from: string;
  to: string;
  repeatsYearly: true;
}

export interface MenuSkin {
  id: string;
  name: string;
  /** A short line a template may show via `${headline}` (e.g. "Merry Christmas"). */
  headline: string;
  description?: string;
  tags?: string[];
  colors: SkinColors;
  /** Storage `template-assets` object key; omitted until the asset exists. */
  decor?: string;
  /** Pre-filled date range for the schedule control; omitted for lunar dates. */
  window?: SkinDefaultWindow;
}

export const MENU_SKINS: readonly MenuSkin[] = [
  {
    id: 'christmas',
    name: 'Christmas',
    headline: 'Merry Christmas',
    description: 'Deep green, gold and red for the festive season.',
    tags: ['winter', 'holiday', 'fixed-date'],
    colors: {
      background: '#0f3d2e',
      surface: '#14523d',
      primary: '#f4d58d',
      text: '#ffffff',
      price: '#f4d58d',
      accent: '#c8102e',
    },
    window: { from: '12-01', to: '01-06', repeatsYearly: true },
  },
  {
    id: 'new-year',
    name: 'New Year',
    headline: 'Happy New Year',
    description: 'Midnight blue with a champagne-gold accent.',
    tags: ['winter', 'holiday', 'fixed-date'],
    colors: {
      background: '#0b1020',
      surface: '#16213a',
      primary: '#f5c451',
      text: '#f5f7ff',
      price: '#f5c451',
      accent: '#c9a227',
    },
    window: { from: '12-26', to: '01-06', repeatsYearly: true },
  },
  {
    id: 'winter-holiday',
    name: 'Winter Holiday',
    headline: "Season's Greetings",
    description: 'Cool, icy blues for a calm winter look.',
    tags: ['winter', 'fixed-date'],
    colors: {
      background: '#f0f4f8',
      surface: '#ffffff',
      primary: '#1d3557',
      text: '#22303f',
      price: '#457b9d',
      accent: '#a8c4dd',
    },
    window: { from: '12-01', to: '01-06', repeatsYearly: true },
  },
  {
    id: 'valentine',
    name: "Valentine's",
    headline: "Happy Valentine's Day",
    description: 'Pinks and a deep rose accent.',
    tags: ['spring', 'fixed-date'],
    colors: {
      background: '#fff0f3',
      surface: '#ffffff',
      primary: '#c9184a',
      text: '#590d22',
      price: '#c9184a',
      accent: '#ff4d6d',
    },
    window: { from: '02-01', to: '02-15', repeatsYearly: true },
  },
  {
    id: 'spring-blossom',
    name: 'Spring Blossom',
    headline: 'Spring is Here',
    description: 'Soft petals and fresh green.',
    tags: ['spring'],
    colors: {
      background: '#fdf2f8',
      surface: '#ffffff',
      primary: '#b03a6a',
      text: '#4a2c3a',
      price: '#ab2b63',
      accent: '#f0a6c8',
    },
    window: { from: '03-01', to: '05-31', repeatsYearly: true },
  },
  {
    id: 'easter',
    name: 'Easter',
    headline: 'Happy Easter',
    description: 'Spring purple with a gold accent.',
    tags: ['spring'],
    colors: {
      background: '#f3f7f0',
      surface: '#ffffff',
      primary: '#7b5ea7',
      text: '#3a2f4a',
      price: '#7b5ea7',
      accent: '#f4c430',
    },
  },
  {
    id: 'mothers-day',
    name: "Mother's Day",
    headline: "Happy Mother's Day",
    description: 'Warm blush and rose.',
    tags: ['spring'],
    colors: {
      background: '#fff0f5',
      surface: '#ffffff',
      primary: '#c2185b',
      text: '#4a1f30',
      price: '#c2185b',
      accent: '#f48fb1',
    },
  },
  {
    id: 'fathers-day',
    name: "Father's Day",
    headline: "Happy Father's Day",
    description: 'Steady blues and a warm amber.',
    tags: ['summer'],
    colors: {
      background: '#eef2f6',
      surface: '#ffffff',
      primary: '#1b3a5b',
      text: '#1f2d3a',
      price: '#2f6fae',
      accent: '#f4a259',
    },
  },
  {
    id: 'independence-gh',
    name: 'Ghana Independence',
    headline: 'Happy Independence Day',
    description: 'The red, gold and green of the Ghanaian flag.',
    tags: ['gh', 'fixed-date'],
    colors: {
      background: '#0b3d2e',
      surface: '#14523d',
      primary: '#fcd116',
      text: '#ffffff',
      price: '#fcd116',
      accent: '#ce1126',
    },
    window: { from: '03-01', to: '03-08', repeatsYearly: true },
  },
  {
    id: 'st-patricks',
    name: "St. Patrick's",
    headline: "Happy St. Patrick's Day",
    description: 'Emerald green and gold.',
    tags: ['spring', 'fixed-date'],
    colors: {
      background: '#0b3d2e',
      surface: '#14523d',
      primary: '#f4c430',
      text: '#ffffff',
      price: '#f4c430',
      accent: '#1b7f3a',
    },
    window: { from: '03-10', to: '03-18', repeatsYearly: true },
  },
  {
    id: 'ramadan',
    name: 'Ramadan',
    headline: 'Ramadan Mubarak',
    description: 'Night blue and lantern gold.',
    tags: ['lunar'],
    colors: {
      background: '#10233a',
      surface: '#16324f',
      primary: '#e8c872',
      text: '#eef3f8',
      price: '#e8c872',
      accent: '#2a9d8f',
    },
  },
  {
    id: 'eid',
    name: 'Eid',
    headline: 'Eid Mubarak',
    description: 'Deep green and warm gold.',
    tags: ['lunar'],
    colors: {
      background: '#0d3b2e',
      surface: '#14513f',
      primary: '#e8c872',
      text: '#f7f3e8',
      price: '#e8c872',
      accent: '#1b7f3a',
    },
  },
  {
    id: 'summer-harvest',
    name: 'Summer Harvest',
    headline: 'Summer Menu',
    description: 'Bright greens and citrus for summer.',
    tags: ['summer'],
    colors: {
      background: '#eaf7ee',
      surface: '#ffffff',
      primary: '#1b7f3a',
      text: '#22331f',
      price: '#0b7a43',
      accent: '#f4c430',
    },
    window: { from: '06-01', to: '08-31', repeatsYearly: true },
  },
  {
    id: 'fourth-of-july',
    name: 'Fourth of July',
    headline: 'Happy 4th of July',
    description: 'Navy, white and red.',
    tags: ['summer', 'fixed-date'],
    colors: {
      background: '#0a3161',
      surface: '#11457f',
      primary: '#ffffff',
      text: '#ffffff',
      price: '#ffd700',
      accent: '#c8102e',
    },
    window: { from: '06-28', to: '07-05', repeatsYearly: true },
  },
  {
    id: 'autumn-harvest',
    name: 'Autumn Harvest',
    headline: 'Harvest Season',
    description: 'Warm amber, rust and cream.',
    tags: ['autumn'],
    colors: {
      background: '#f4e3cf',
      surface: '#fff6ea',
      primary: '#8a4b1e',
      text: '#4a2c14',
      price: '#a34d0f',
      accent: '#d2691e',
    },
    window: { from: '09-01', to: '11-30', repeatsYearly: true },
  },
  {
    id: 'halloween',
    name: 'Halloween',
    headline: 'Happy Halloween',
    description: 'Pumpkin orange and violet on a dark field.',
    tags: ['autumn', 'fixed-date'],
    colors: {
      background: '#1a1423',
      surface: '#2a1f33',
      primary: '#ff7a00',
      text: '#f5f1e6',
      price: '#ff7a00',
      accent: '#7b2cbf',
    },
    window: { from: '10-20', to: '11-01', repeatsYearly: true },
  },
  {
    id: 'thanksgiving',
    name: 'Thanksgiving',
    headline: 'Happy Thanksgiving',
    description: 'Warm cream and spiced brown.',
    tags: ['autumn', 'fixed-date'],
    colors: {
      background: '#f5e6cf',
      surface: '#fff8ee',
      primary: '#8a4b1e',
      text: '#4a2c14',
      price: '#9c531a',
      accent: '#c0582a',
    },
    window: { from: '11-15', to: '11-30', repeatsYearly: true },
  },
  {
    id: 'festival-lights',
    name: 'Festival Lights',
    headline: 'Festival of Lights',
    description: 'A dark evening with warm festive lights.',
    tags: ['winter'],
    colors: {
      background: '#1a1030',
      surface: '#2a1a4a',
      primary: '#ffd166',
      text: '#f7f3ff',
      price: '#ffd166',
      accent: '#ef476f',
    },
  },
];

export const MENU_SKIN_IDS: ReadonlySet<string> = new Set(MENU_SKINS.map((skin) => skin.id));

export function getMenuSkin(id: string | null | undefined): MenuSkin | undefined {
  if (!id) return undefined;
  return MENU_SKINS.find((skin) => skin.id === id);
}

/** The chip shown in the Skin tab: background, accent and text sample colours. */
export function skinSwatch(skin: MenuSkin): { background: string; accent: string; text: string } {
  return {
    background: skin.colors.background || '#ffffff',
    accent: skin.colors.accent || skin.colors.primary || '#111111',
    text: skin.colors.text || skin.colors.primary || '#111111',
  };
}

/**
 * A skin's colours are a *default layer*: the merchant's explicit colour
 * overrides (same-named slots) win. Returns a fresh object so callers never
 * mutate the catalog entry.
 */
export function mergeSkinColors(skin: MenuSkin, explicit?: SkinColors): SkinColors {
  const colors = skin.colors;
  return {
    secondary: colors.surface,
    muted: colors.text,
    divider: colors.accent,
    promoBg: colors.surface,
    promoText: colors.text,
    promoBorder: colors.accent,
    availabilityBg: colors.surface,
    availabilityText: colors.text,
    hero: colors.background,
    ...colors,
    ...(explicit || {}),
  };
}

/** Validates that every colour a skin defines is one of the six design slots. */
export function isSkinColorKey(key: string): key is DesignColorKey {
  return (DESIGN_COLOR_KEYS as readonly string[]).includes(key);
}
